/**
 * Main site header for logged-in users
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { uiState } from 'react-redux-ui-state';
import { withStyles } from '@material-ui/styles';
import AppBar from '@material-ui/core/AppBar';
import MenuIcon from '@material-ui/icons/Menu';
import HelpIcon from '@material-ui/icons/Help';
import AccountCircle from '@material-ui/icons/AccountCircle';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';

import WithSpecifiedTheme from '../../WithSpecifiedTheme/WithSpecifiedTheme';
import Menu from './Menu/Menu';
import { darkTheme } from '../../App/App.theme';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    tagline: PropTypes.string.isRequired,
    branding: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    navToggle: PropTypes.string.isRequired,
    menu: PropTypes.string.isRequired,
    menuToggle: PropTypes.string.isRequired,
  }).isRequired,
  handleNavToggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  menuAnchorEl: PropTypes.shape({}),
  setUiState: PropTypes.func.isRequired,
  user: PropTypes.shape({}).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isORv4Admin: PropTypes.bool.isRequired,
};

const defaultProps = {
  className: null,
  menuAnchorEl: null,
};

export class UnstyledHeader extends React.Component {
  constructor(props) {
    super(props);
    this.onResize = this.onResize.bind(this);
    this.toggleNav = this.toggleNav.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    const { menuAnchorEl, setUiState } = this.props;
    if (menuAnchorEl) {
      setUiState({ menuAnchorEl: null });
    }
  }

  openMenu(event) {
    const { setUiState } = this.props;
    setUiState({ menuAnchorEl: event.currentTarget });
  }

  toggleNav() {
    const { handleNavToggle, isOpen } = this.props;
    handleNavToggle(!isOpen);
  }

  closeMenu() {
    const { setUiState } = this.props;
    setUiState({ menuAnchorEl: null });
  }

  render() {
    const { className, classes, menuAnchorEl, user, isAdmin, isORv4Admin } = this.props;

    return (
      <AppBar className={classnames(className, classes.root)} position="absolute">
        <IconButton
          className={classes.navToggle}
          aria-label="Toggle Navigation"
          onClick={this.toggleNav}
        >
          <MenuIcon />
        </IconButton>
        <div className={classes.branding}>
          <Link to="/" className={classes.logo}>
            <img
              src="/images/logos/or-logo-white-no-tagline.svg"
              alt="ObjectRocket - A Rackspace Company"
            />
          </Link>
          <Typography variant="h6" className={classes.tagline}>
            Mission Control
          </Typography>
        </div>
        <Menu
          className={classes.menu}
          anchorEl={menuAnchorEl}
          onClose={this.closeMenu}
          user={user}
          isAdmin={isAdmin}
          isORv4Admin={isORv4Admin}
        />
        <div className={classes.menuToggle}>
          <IconButton
            component="a"
            href="https://objectrocket.zendesk.com/hc/en-us"
            target="_blank"
            rel="noopener noreferrer"
          >
            <HelpIcon fontSize="large" />
          </IconButton>
          <IconButton aria-label="Toggle Menu" onClick={this.openMenu}>
            <AccountCircle fontSize="large" />
          </IconButton>
        </div>
      </AppBar>
    );
  }
}

const styles = (theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: theme.customColors.black,
  },
  tagline: {
    color: theme.customColors.orange,
  },
  branding: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
  logo: {
    '& img': {
      height: '100%',
    },
  },
  orgIndicator: {
    maxHeight: '90%',
  },
  [theme.breakpoints.down('sm')]: {
    root: {
      position: 'fixed',
      flexDirection: 'row',
    },
    branding: {
      flexDirection: 'column',
      textAlign: 'center',
      margin: '0 auto',
      flex: 2,
    },
    navToggle: {
      position: 'absolute',
      top: theme.spacing(1),
    },
    logo: {
      margin: '0 auto',
      height: '47%',
    },
    tagline: {
      fontSize: '.857rem',
    },
    menuToggle: {
      display: 'none',
    },
    menu: {
      display: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      alignContent: 'space-between',
    },
    navToggle: {
      display: 'none',
    },
    logo: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(1),
      height: '50%',
    },
    branding: {
      flexDirection: 'row',
      alignItems: 'center',
      alignContent: 'flex-start',
    },
    menuToggle: {
      textAlign: 'right',
    },
  },
});

UnstyledHeader.propTypes = propTypes;
UnstyledHeader.defaultProps = defaultProps;

export const uiConfig = {
  persist: true,
  name: 'header',
  state: () => ({
    menuAnchorEl: null,
  }),
};

export default uiState(uiConfig)(withStyles(styles)(WithSpecifiedTheme(darkTheme)(UnstyledHeader)));
